import { IntlShape } from 'react-intl';

import { Item, Category, Option, Translation } from 'apis/restaurant-api/merchant-admin/menu2/types';

export type PartialExportedMenu = {
  items: Item[];
  menu: {
    categories: Category[];
  };
  options: Option[];
};

const isExportedMenu = (json: any): json is PartialExportedMenu => {
  return !!json?.items && !!json?.menu && !!json?.options;
};

export const readAndParseFile = (file: File, intl: IntlShape): Promise<PartialExportedMenu> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result ? reader.result.toString() : '';
      let json;
      try {
        json = JSON.parse(result);
      } catch (error) {
        reject(intl.formatMessage({ id: 'menu.importErrorJsonParsing' }));
      }
      if (!isExportedMenu(json)) {
        reject(intl.formatMessage({ id: 'menu.importErrorNotMenuFile' }));
      }
      resolve(json);
    };
    reader.onerror = () => {
      reject(intl.formatMessage({ id: 'menu.importErrorReadingFile' }));
    };
    reader.readAsText(file);
  });
};

const filterInvalidLocalisedValues = (translations: Translation[] | undefined) =>
  translations ? translations.filter(t => !!t.value.trim()) : [];

export const removeInvalidLocalisedValues = (menuJson: PartialExportedMenu): PartialExportedMenu => {
  return {
    ...menuJson,
    items: menuJson.items.map(item => ({
      ...item,
      additives: filterInvalidLocalisedValues(item.additives),
      allergens: filterInvalidLocalisedValues(item.allergens),
      name: filterInvalidLocalisedValues(item.name),
      description: filterInvalidLocalisedValues(item.description),
      distributor_information: filterInvalidLocalisedValues(item.distributor_information),
      producer_information: filterInvalidLocalisedValues(item.producer_information),
      nutrition_facts: filterInvalidLocalisedValues(item.nutrition_facts),
      ingredients: filterInvalidLocalisedValues(item.ingredients),
      country_of_origin: filterInvalidLocalisedValues(item.country_of_origin),
      conditions_of_use_and_storage: filterInvalidLocalisedValues(item.conditions_of_use_and_storage),
    })),
    menu: {
      ...menuJson.menu,
      categories: menuJson.menu.categories.map(category => ({
        ...category,
        name: filterInvalidLocalisedValues(category.name),
        description: filterInvalidLocalisedValues(category.description),
      })),
    },
    options: menuJson.options.map(option => ({
      ...option,
      values: option.values.map(optionValue => ({
        ...optionValue,
        additives: filterInvalidLocalisedValues(optionValue.additives),
        allergens: filterInvalidLocalisedValues(optionValue.allergens),
        name: filterInvalidLocalisedValues(optionValue.name),
        distributor_information: filterInvalidLocalisedValues(optionValue.distributor_information),
        producer_information: filterInvalidLocalisedValues(optionValue.producer_information),
        nutrition_facts: filterInvalidLocalisedValues(optionValue.nutrition_facts),
        ingredients: filterInvalidLocalisedValues(optionValue.ingredients),
      })),
    })),
  };
};
